import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, ArrowUpRight, ExternalLink } from "lucide-react";
import Logo from "./Logo";
import { footerLinks } from "@/config/footerConfig";
import { contactConfig } from "@/config/contactConfig";
import { companyConfig } from "@/config/companyConfig";
import { FOOTER } from "@/constants/testIds";

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const Footer = () => {
  return (
    <footer className="relative bg-[hsl(var(--navy-deep))] text-white/80 overflow-hidden" data-testid={FOOTER.root}>
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-px inset-x-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--cyan))]/40 to-transparent" />

      <div className="relative container-px mx-auto max-w-7xl py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand + map */}
          <div className="lg:col-span-5 flex flex-col">
            <Logo height={40} />
            <p className="mt-5 text-sm leading-relaxed text-white/65 max-w-md">
              Procesadora regional de medios de pago para instituciones financieras en Latinoamérica. Implementación llave en mano · procesamiento · digitalización · seguridad · operación 24/7.
            </p>

            <Link
              to="/contacto"
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-full border border-[hsl(var(--cyan))]/30 bg-[hsl(var(--cyan))]/10 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-[hsl(var(--cyan))]/60"
            >
              Hablemos de tu proyecto de pagos <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>

            <div className="mt-7 rounded-xl overflow-hidden border border-white/10 shadow-card-soft">
              <iframe
                title="Ubicación Captec en Google Maps"
                src={contactConfig.mapsEmbedUrl}
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid={FOOTER.mapsIframe}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--cyan))]">Navegación</h4>
            <ul className="mt-5 space-y-3">
              {footerLinks.navigation.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={FOOTER.navLink(slugify(l.label))}
                    className="group inline-flex items-center text-sm text-white/75 hover:text-[hsl(var(--cyan))] transition-colors"
                  >
                    <span className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-[hsl(var(--cyan))] mr-2 transition-colors" />
                    {l.label}
                  </Link>
                </li>
              ))}
              {footerLinks.external.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-1.5 text-sm text-white/75 hover:text-[hsl(var(--cyan))] transition-colors"
                  >
                    <span className="w-1 h-1 rounded-full bg-white/30 group-hover:bg-[hsl(var(--cyan))] mr-1 transition-colors" />
                    {l.label}
                    <ExternalLink className="w-3 h-3 opacity-70" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4 space-y-6">
            <h4 className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--cyan))]">Contacto</h4>
            <div>
              <p className="text-sm font-semibold text-white">{contactConfig.tech.label}</p>
              <div className="mt-2 space-y-1.5 text-sm">
                {contactConfig.tech.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/75 hover:text-[hsl(var(--cyan))]">
                    <Phone className="w-3.5 h-3.5" /> {p}
                  </a>
                ))}
                <a href={`mailto:${contactConfig.tech.email}`} className="flex items-center gap-2 text-white/75 hover:text-[hsl(var(--cyan))]">
                  <Mail className="w-3.5 h-3.5" /> {contactConfig.tech.email}
                </a>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-white">{contactConfig.commercial.label}</p>
              <div className="mt-2 space-y-1.5 text-sm">
                {contactConfig.commercial.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/75 hover:text-[hsl(var(--cyan))]">
                    <Phone className="w-3.5 h-3.5" /> {p}
                  </a>
                ))}
                <a href={`mailto:${contactConfig.commercial.email}`} className="flex items-center gap-2 text-white/75 hover:text-[hsl(var(--cyan))]">
                  <Mail className="w-3.5 h-3.5" /> {contactConfig.commercial.email}
                </a>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-white">Dirección</p>
              <p className="mt-2 text-sm text-white/75 leading-relaxed flex gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {contactConfig.address.line1}.<br />
                  {contactConfig.address.line2}.
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} Captec. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-3">
            <a
              href={companyConfig.serviceDeskUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-[hsl(var(--cyan))] transition-colors"
            >
              Soporte 24/7 <ArrowUpRight className="w-3 h-3" />
            </a>
            {companyConfig.linkedinUrl ? (
              <a href={companyConfig.linkedinUrl} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="p-2 rounded-md text-white/70 hover:text-[hsl(var(--cyan))] hover:bg-white/5">
                <Linkedin className="w-4 h-4" />
              </a>
            ) : (
              <span title="LinkedIn pendiente de configuración" className="p-2 rounded-md text-white/30">
                <Linkedin className="w-4 h-4" />
              </span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
